import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid } from '@mui/material';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { ControlledField, LocationInputField } from 'remote/global';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { hasReadOnlyPermission } from 'utils/helpers';
import {
  VehicleLocationItemSchemaType,
  VehicleLocationSchemaType,
  vehicleLocationSchema,
} from './types';

type Props = {
  onClose: () => void;
  defaultValues?: Partial<VehicleLocationSchemaType>;
  onConfirm: (location: VehicleLocationItemSchemaType) => void;
};

function VehicleChangeLocationForm(props: Props) {
  const { onConfirm, defaultValues } = props;
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const tInstance = useTranslation();
  const { t } = tInstance;

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<VehicleLocationSchemaType>({
    defaultValues: {
      location: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleLocationSchema),
  });

  const { permissions } = userInfo ?? {};
  const isLocationFormDisabled = hasReadOnlyPermission(permissions?.VEHICLE_LOCATION ?? '');

  const onValid = (data: VehicleLocationSchemaType) => {
    onConfirm(data.location);
  };

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ControlledField
            control={control}
            name='location'
            disabled={isLocationFormDisabled}
            render={(renderProps) => (
              <LocationInputField
                {...renderProps}
                focusInitial
                fieldProps={{
                  disableClearable: true,
                }}
                googleConfig={googleConfig!}
              />
            )}
          />
          <Button
            type='submit'
            variant='contained'
            disabled={isLoading || isSubmitting || isValidating || isLocationFormDisabled}
            fullWidth
            sx={{ mt: '2rem' }}
          >
            {t('save')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default VehicleChangeLocationForm;
